import { SeriesConfig } from './seriesConfig';

/**
 * Given a date range and a table of [thresholdMinutes, resolutionMinutes] steps,
 * return the appropriate resolution. Used by both the ConvergenceChart (candle
 * resolution) and the timeseries client (server-side downsampling).
 *
 * A resolution of 0 means "no downsampling".
 */
export function autoResolutionMinutes(
    range: [Date, Date],
    steps: readonly [number, number][],
    fallback: number,
): number {
    const spanMin = (range[1].getTime() - range[0].getTime()) / 60_000;
    for (const [threshold, res] of steps) {
        if (spanMin <= threshold) return res;
    }
    return fallback;
}

/** Steps for timeseries data (hourly base density). Keeps rows under ~5000. */
export const TIMESERIES_RESOLUTION_STEPS: readonly [number, number][] = [
    [10080, 0],       // <=7d   -> no downsampling (~168 rows)
    [43200, 60],      // <=30d  -> 1h (~720 rows)
    [129600, 180],    // <=90d  -> 3h (~720 rows)
    [259200, 360],    // <=180d -> 6h (~720 rows)
    [525600, 720],    // <=1y   -> 12h (~730 rows)
    [1051200, 1440],  // <=2y   -> 1d (~730 rows)
];
// >2y fallback: 1d (1440 min)

/**
 * Aliases for tickers that appear with different names in series.
 * Keys are lowercase tickers, values are all lowercase search terms.
 */
const TICKER_ALIASES: Record<string, string[]> = {
    btc: ['btc', 'bitcoin'],
    eth: ['eth', 'ethereum'],
    ada: ['ada', 'cardano'],
    doge: ['doge', 'dogecoin'],
    link: ['link', 'chainlink'],
    ltc: ['ltc', 'litecoin'],
    xrp: ['xrp', 'ripple'],
    bnb: ['bnb'],
};

/**
 * Return all series whose id contains the given ticker.
 * Uses the same alias expansion as matchSeriesByTicker.
 */
export function filterSeriesByTicker(
    series: SeriesConfig[],
    ticker: string,
): SeriesConfig[] {
    const lower = ticker.toLowerCase();
    const searchTerms = TICKER_ALIASES[lower] || [lower];
    return series.filter(config => {
        const id = config.id.toLowerCase();
        return searchTerms.some(term => id.includes(term));
    });
}

/**
 * Find the first series whose id (raw name) contains the given ticker.
 * Uses case-insensitive substring matching with alias expansion.
 *
 * @returns The matching SeriesConfig, or null if no match found.
 */
export function matchSeriesByTicker(
    series: SeriesConfig[],
    ticker: string,
): SeriesConfig | null {
    const lower = ticker.toLowerCase();
    const searchTerms = TICKER_ALIASES[lower] || [lower];

    for (const config of series) {
        const id = config.id.toLowerCase();
        if (searchTerms.some(term => id.includes(term))) {
            return config;
        }
    }
    return null;
}
